import { useMemo, useState } from 'react';
import { ChevronDown, Search, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/cn';
import { contaStati, dataOraIt, filtraPartecipanti, statoPartecipante, type StatoPartecipante } from '../../lib/eventi/stati';
import type { PartecipanteEvento } from '../../lib/eventi/tipi';
import type { ProgrammaEvento } from '@eventi/tipi.ts';
import { BadgeStato } from './BadgeStato';
import { ContatoriStati } from './ContatoriStati';
import { Iniziali } from './Iniziali';
import { Pannello } from './Pannello';
import { TabellaPercorso } from './TabellaPercorso';
import type { SchedaEvento } from './schede';

type Props = {
  programma: ProgrammaEvento;
  partecipanti: PartecipanteEvento[];
  onApri: (scheda: SchedaEvento) => void;
};

/** Quando e' successa l'ultima cosa che conta: l'invio della richiesta o la conferma. */
function quando(p: PartecipanteEvento): string | null {
  const stato = statoPartecipante(p);
  if (stato === 'confermata') return p.confermata_at ? `il ${dataOraIt(p.confermata_at)}` : null;
  if (stato === 'richiesta') return p.richiesta_at ? `il ${dataOraIt(p.richiesta_at)}` : null;
  return null;
}

function Stato({ p, className }: { p: PartecipanteEvento; className?: string }) {
  const data = quando(p);
  return (
    <span className={cn('flex flex-wrap items-center gap-x-2 gap-y-0.5', className)}>
      <BadgeStato partecipante={p} />
      {data && <span className="text-[11px] text-neutral-400">{data}</span>}
    </span>
  );
}

function Riga({ p, programma, aperta, onApri }: { p: PartecipanteEvento; programma: ProgrammaEvento; aperta: boolean; onApri: () => void }) {
  return (
    <li className="border-t border-gray-100 first:border-t-0">
      <button
        type="button"
        onClick={onApri}
        aria-expanded={aperta}
        className={cn(
          'grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 px-4 py-3 md:px-5 text-left transition-colors hover:bg-gray-50',
          'md:grid-cols-[auto_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto]',
          aperta && 'bg-gray-50',
        )}
      >
        <Iniziali nome={p.nome} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-gray-900">{p.nome}</span>
          {p.azienda && <span className="block truncate text-[11px] text-neutral-500">{p.azienda}</span>}
          <Stato p={p} className="mt-1 md:hidden" />
        </span>
        <span className="hidden truncate text-xs text-neutral-600 md:block">{p.email}</span>
        <Stato p={p} className="hidden md:flex" />
        <ChevronDown className={cn('h-4 w-4 text-neutral-400 transition-transform', aperta && 'rotate-180')} />
      </button>
      {aperta && (
        <div className="bg-gray-50 px-4 pb-4 md:pl-16 md:pr-5">
          <p className="mb-2 truncate text-xs text-neutral-600 md:hidden">{p.email}</p>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            {p.richiesta ? (
              <TabellaPercorso programma={programma} richiesta={p.richiesta} assegnazione={p.assegnazione_stato === 'nessuna' ? null : p.assegnazione} />
            ) : (
              <p className="text-xs text-neutral-500">
                Non ha ancora inviato la sua scelta.
                {p.assegnazione_stato !== 'nessuna' && " L'assegnazione è stata fatta a mano: la trovi nella scheda Assegnazioni."}
              </p>
            )}
            {p.nota && <p className="mt-2 text-xs text-neutral-600">Nota: {p.nota}</p>}
          </div>
        </div>
      )}
    </li>
  );
}

/** La scheda Richieste: chi ha scelto, cosa e quando; chi non ha ancora scelto. */
export function SchedaRichieste({ programma, partecipanti, onApri }: Props) {
  const [stato, setStato] = useState<StatoPartecipante | 'tutti'>('tutti');
  const [testo, setTesto] = useState('');
  const [aperte, setAperte] = useState<Set<string>>(new Set());

  const conti = useMemo(() => contaStati(partecipanti), [partecipanti]);
  const visibili = useMemo(() => filtraPartecipanti(partecipanti, { stato, testo }), [partecipanti, stato, testo]);

  const apri = (id: string) =>
    setAperte((prima) => {
      const dopo = new Set(prima);
      if (dopo.has(id)) dopo.delete(id);
      else dopo.add(id);
      return dopo;
    });

  if (partecipanti.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white py-14 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-neutral-400">
          <Users className="h-6 w-6" />
        </span>
        <p className="mt-3 text-sm font-medium text-neutral-700">Nessun partecipante ancora</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-neutral-500">
          Scegli la lista dei partecipanti nelle Impostazioni: chi c'è dentro compare qui.
        </p>
        <Button variant="outline" className="mt-4" onClick={() => onApri('impostazioni')}>
          Vai alle impostazioni
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ContatoriStati conti={conti} attivo={stato} onScegli={setStato} />

      <Pannello
        titolo="Partecipanti"
        sotto="Tocca una persona per vedere cosa ha chiesto e, se c'è, cosa le è stato assegnato."
        azioni={
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Cerca per nome, email o azienda" className="h-9 pl-9" />
          </div>
        }
        corpo="p-0 pt-3 md:p-0 md:pt-3"
      >
        <div className="hidden grid-cols-[auto_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto] gap-x-3 border-y border-gray-100 bg-gray-50/60 px-4 py-2 text-[11px] uppercase tracking-wider text-neutral-500 md:grid md:px-5">
          <span className="w-8" />
          <span>Nome</span>
          <span>Email</span>
          <span>Stato</span>
          <span className="w-4" />
        </div>
        {visibili.length === 0 ? (
          <p className="py-10 text-center text-sm text-neutral-500">Nessuno corrisponde al filtro.</p>
        ) : (
          <ul className="border-t border-gray-100 md:border-t-0">
            {visibili.map((p) => (
              <Riga key={p.id} p={p} programma={programma} aperta={aperte.has(p.id)} onApri={() => apri(p.id)} />
            ))}
          </ul>
        )}
      </Pannello>
      <p className="text-[11px] text-neutral-400">
        La richiesta si invia una volta sola dalla pagina di scelta. Per cambiarla la persona scrive all'email di
        riferimento e voi cambiate l'assegnazione nella scheda Assegnazioni.
      </p>
    </div>
  );
}
